import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import {
  MapPin, ShieldCheck, ShieldAlert, ShieldQuestion, Loader2, RefreshCw, Sparkles, UserPlus, Repeat,
  Home, Building2, Warehouse, Mailbox, Truck as TruckIcon,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 25-oct: this ran on every keystroke (address check, map and AI fraud), and
// EasyPost rate-limited the account. It now waits until typing stops, and the
// address is only checked again when the address itself changes.
const WAIT_MS = 1200;

/**
 * DestinationPreview — shown next to the recipient card on every shipping page.
 *
 * Combines:
 *   1. Where the address lands (Photon / OpenStreetMap geocode, free, no key)
 *   2. EasyPost address verification (already configured)
 *   3. AI fraud analysis (freight-forwarder, mail-drop, address velocity, Claude explanation)
 *
 * Props:
 *   address       { name, company, street1, street2, city, state, zip, country }
 *   declaredValue number — passed to fraud analyzer
 *   onFraud       (result) => void   — parent can gate the label button
 */
export default function DestinationPreview({ address, declaredValue = 0, onFraud }) {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [map, setMap] = useState(null);
  const [verify, setVerify] = useState(null);
  const [fraud, setFraud] = useState(null);
  const [busy, setBusy] = useState(false);
  const checkedKey = useRef(null);

  const addressReady =
    address && address.street1 && (address.zip || address.city) && address.state;

  const addressKey = [
    address?.street1, address?.street2, address?.city, address?.state, address?.zip, address?.country,
  ].map((v) => (v || '').trim().toUpperCase()).join('|');

  // force: the refresh button checks the address again even if it did not change.
  const load = async (force = false) => {
    if (!addressReady) return;
    setBusy(true);
    try {
      if (force || checkedKey.current !== addressKey) {
        const query = `${address.street1}, ${address.city || ''}, ${address.state || ''} ${address.zip || ''}, ${address.country || 'US'}`;
        // 1. Address verification (EasyPost — US + international)
        const verRes = await fetch(`${API}/shipping/addresses/verify`, {
          method: 'POST', headers: auth,
          body: JSON.stringify({
            name: address.name, company: address.company,
            street1: address.street1, street2: address.street2,
            city: address.city, state: address.state,
            zip: address.zip, country: address.country || 'US',
          }),
        });
        // A failed check says nothing about the address: it is "unavailable", not "not deliverable".
        setVerify(verRes.ok
          ? await verRes.json()
          : { valid: null, unavailable: true, errors: [{ message: 'The address check did not answer.' }] });
        // 2. Where the address lands (Photon geocode)
        const mapRes = await fetch(`${API}/geo/map-preview?q=${encodeURIComponent(query)}`);
        setMap(mapRes.ok ? await mapRes.json() : null);
        checkedKey.current = addressKey;
      }
      // 3. AI fraud analysis
      const fRes = await fetch(`${API}/fraud/analyze`, {
        method: 'POST', headers: auth,
        body: JSON.stringify({
          to_address: address,
          declared_value: Number(declaredValue) || 0,
          include_ai: true,
        }),
      });
      const f = fRes.ok ? await fRes.json() : null;
      setFraud(f);
      onFraud && onFraud(f);
    } catch (e) {
      // silent — parent still owns the label button
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!addressReady) return undefined;
    const timer = setTimeout(() => load(), WAIT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressKey, declaredValue]);

  if (!addressReady) {
    return (
      <Card className="border-dashed" data-testid="destination-preview-empty">
        <CardContent className="pt-6 text-center text-xs text-slate-400">
          Enter the recipient address to preview the destination.
        </CardContent>
      </Card>
    );
  }

  const StIcon = fraud?.decision === 'block' ? ShieldAlert : fraud?.decision === 'review' ? ShieldQuestion : ShieldCheck;
  const stCls = fraud?.decision === 'block'
    ? 'text-rose-600 bg-rose-50 dark:bg-rose-900/20'
    : fraud?.decision === 'review'
      ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20'
      : 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20';

  return (
    <Card data-testid="destination-preview">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-500" /> Destination preview
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => load(true)} disabled={busy} data-testid="dest-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Where the address lands. The map itself is the Google preview on the
            page; the static image this used came from a service that no longer
            exists and showed broken. */}
        {map?.display_name && (
          <div className="text-[11px] text-slate-500 truncate" title={map.display_name} data-testid="dest-found">
            📍 {map.display_name}
          </div>
        )}

        {/* Address verify */}
        {verify && (
          <div className="flex items-start gap-2 text-xs" data-testid="dest-verify">
            {verify.unavailable ? (
              <>
                <ShieldQuestion className="w-4 h-4 text-amber-500 mt-0.5" />
                <div className="text-amber-700" data-testid="dest-verify-unavailable">
                  Address check unavailable right now{verify.errors?.[0]?.message ? ` — ${verify.errors[0].message}` : ''}
                </div>
              </>
            ) : verify.valid ? (
              <>
                <ShieldCheck className="w-4 h-4 text-emerald-500 mt-0.5" />
                <div>
                  <div className="font-medium text-emerald-700">Address verified deliverable</div>
                  {verify.changed_fields?.length ? (
                    <div className="text-[11px] text-amber-600">Corrected: {verify.changed_fields.join(', ')}</div>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <ShieldAlert className="w-4 h-4 text-rose-500 mt-0.5" />
                <div className="text-rose-700">
                  Not deliverable {verify.errors?.[0]?.message ? `— ${verify.errors[0].message}` : ''}
                </div>
              </>
            )}
          </div>
        )}

        {/* First-time / repeat recipient chip */}
        {fraud?.identity_binding && (fraud.identity_binding.bound !== undefined) && (
          <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="dest-buyer-chip">
            {fraud.identity_binding.bound ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-medium">
                <Repeat className="w-3 h-3" />
                Repeat recipient · {fraud.identity_binding.prior_shipments} prior shipment{fraud.identity_binding.prior_shipments === 1 ? '' : 's'}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-medium">
                <UserPlus className="w-3 h-3" />
                New buyer · first shipment to this recipient
              </span>
            )}
            {fraud.address_type?.label && (() => {
              const { label, confidence, rationale } = fraud.address_type;
              const cfg = {
                residential:       { Icon: Home,       cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',       text: 'Residential' },
                commercial:        { Icon: Building2,  cls: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',       text: 'Commercial' },
                warehouse:         { Icon: Warehouse,  cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300', text: 'Warehouse' },
                mail_drop:         { Icon: Mailbox,    cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300', text: 'Mail drop' },
                freight_forwarder: { Icon: TruckIcon,  cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',         text: 'Freight forwarder' },
                unknown:           { Icon: MapPin,     cls: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',       text: 'Unclassified' },
              }[label] || { Icon: MapPin, cls: 'bg-slate-100 text-slate-500', text: label };
              const IconEl = cfg.Icon;
              return (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full font-medium ${cfg.cls}`} data-testid="dest-address-type" title={`${rationale} · confidence ${Math.round(confidence * 100)}%`}>
                  <IconEl className="w-3 h-3" />
                  {cfg.text}
                  <span className="opacity-60 ml-0.5">{Math.round(confidence * 100)}%</span>
                </span>
              );
            })()}
          </div>
        )}

        {/* AI fraud */}
        {fraud && (
          <div className={`rounded-lg p-2.5 ${stCls}`} data-testid="dest-fraud">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
              <StIcon className="w-3.5 h-3.5" />
              {fraud.decision === 'block' ? 'High risk — blocked'
                : fraud.decision === 'review' ? 'Needs review'
                  : 'Low risk'}
              <span className="ml-auto text-[10px] font-normal opacity-70">risk {fraud.risk_score}</span>
            </div>
            {fraud.ai_explanation && (
              <p className="text-xs mt-1.5 flex items-start gap-1.5">
                <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-60" />
                {fraud.ai_explanation}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
